import safeTrack from 'src/utils/safe-track'

// Settings sent with their value; the rest (colors, logos, sizes, copyright)
// only say which setting changed.
const BOOLEAN_SETTINGS = [
  'showSignIn',
  'showHeader',
  'stickyHeader',
  'showBreadcrumbs',
  'showFooter',
]
const DEFAULT_ON = ['showHeader', 'showBreadcrumbs', 'showFooter']
const VALUE_SETTINGS = ['preset', 'headerFont', 'footerFont']
const OTHER_SETTINGS = [
  'headerBackgroundColor',
  'headerTextColor',
  'headerLogo',
  'headerLogoSize',
  'headerTextSize',
  'footerBackgroundColor',
  'footerTextColor',
  'footerLogo',
  'footerLogoSize',
  'footerTextSize',
  'copyright',
]

const byKey = (items, key) =>
  new Map(
    (items || []).filter(item => item[key]).map(item => [item[key], item]),
  )

/**
 * Sends Amplitude events for what changed between two saved portals
 * (Portal › Navigation and Footer save every change through one call).
 */
const trackPortalChanges = (before, after) => {
  if (!before || !after) return

  // Missing means on for these, as the admin reads them.
  const isOn = (portal, setting) =>
    DEFAULT_ON.includes(setting)
      ? portal[setting] !== false
      : Boolean(portal[setting])
  BOOLEAN_SETTINGS.forEach(setting => {
    if (isOn(before, setting) !== isOn(after, setting))
      safeTrack('changed_portal_setting', {
        setting,
        value: isOn(after, setting),
      })
  })
  VALUE_SETTINGS.forEach(setting => {
    if ((before[setting] || '') !== (after[setting] || ''))
      safeTrack('changed_portal_setting', {
        setting,
        value: after[setting] || null,
      })
  })
  OTHER_SETTINGS.forEach(setting => {
    if ((before[setting] ?? '') !== (after[setting] ?? ''))
      safeTrack('changed_portal_setting', { setting })
  })

  // Menu: external links added, pages shown or hidden.
  const pages = byKey(after.pages, 'id')
  const menuBefore = byKey(before.menuItems, 'id')
  const pagesBefore = byKey(
    (before.menuItems || []).filter(item => item.type === 'page'),
    'pageId',
  )
  ;(after.menuItems || []).forEach(item => {
    if (item.type === 'link' && !menuBefore.has(item.id)) {
      safeTrack('added_portal_menu_link', { type: 'external' })
    }
    if (item.type === 'page') {
      const was = pagesBefore.get(item.pageId)
      const wasShown = Boolean(was && was.visible !== false)
      const isShown = item.visible !== false
      if (wasShown !== isShown)
        safeTrack('toggled_portal_menu_item', {
          page_type: pages.get(item.pageId)?.type || null,
          shown: isShown,
        })
    }
  })

  // Footer links and social profiles added.
  const footerBefore = byKey(before.footerLinks, 'id')
  ;(after.footerLinks || []).forEach(link => {
    if (!footerBefore.has(link.id))
      safeTrack('added_portal_footer_link', {
        type: link.pageId ? 'page' : 'external',
      })
  })
  const socialsBefore = byKey(before.socialLinks, 'network')
  ;(after.socialLinks || []).forEach(social => {
    if (!socialsBefore.has(social.network))
      safeTrack('added_portal_social', { network: social.network })
  })
}

export default trackPortalChanges
